import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { format } from 'date-fns';

const CustomerInspections = () => {
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('ALL');
  const [subscription, setSubscription] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestForm, setRequestForm] = useState({
    propertyType: '',
    urgency: 'normal',
    description: '',
    scheduledDate: '',
    scheduledTime: ''
  });
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [bookedSlots, setBookedSlots] = useState([]);
  const navigate = useNavigate();

  // Generate time slots from 9 AM to 6 PM (30-minute intervals)
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 9; hour < 18; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
      slots.push(`${hour.toString().padStart(2, '0')}:30`);
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  const fetchAvailableSlots = useCallback(async (date) => {
    try {
      const response = await api.get(`/inspections/slots?date=${date}`);
      setBookedSlots(response.bookedSlots || []);
    } catch (error) {
      console.error('Error fetching available slots:', error);
      setBookedSlots([]);
    }
  }, []);

  const isSlotBooked = (time) => {
    if (!selectedDate) return false;
    return bookedSlots.includes(`${selectedDate}T${time}:00`);
  };

  const fetchInspections = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = filter !== 'ALL' ? { status: filter } : {};
      const response = await api.get('/inspections/customer', { params });
      
      // Ensure we have valid data
      const inspectionsData = response.data?.data || [];
      setInspections(Array.isArray(inspectionsData) ? inspectionsData : []);
    } catch (error) {
      console.error('Error fetching inspections:', error);
      setError('Failed to load inspections');
      setInspections([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const fetchSubscription = useCallback(async () => {
    try {
      console.log('🔍 Fetching subscription...');
      const token = localStorage.getItem('token');
      console.log('🔍 Token exists:', !!token);
      console.log('🔍 Token length:', token?.length || 0);

      if (!token) {
        console.log('❌ No token found - user not logged in');
        setSubscription(null);
        return;
      }

      if (token) {
        try {
          // Try to decode the token (without verification)
          const payload = JSON.parse(atob(token.split('.')[1]));
          console.log('🔍 Decoded token payload:', payload);
          console.log('🔍 User ID from token:', payload.userId);
          console.log('🔍 Token expires:', new Date(payload.exp * 1000));
          console.log('🔍 Token expired:', payload.exp * 1000 < Date.now());
        } catch (e) {
          console.log('🔍 Could not decode token:', e.message);
        }
      }

      console.log('🔍 Making API request to /membership/my-membership');
      const response = await api.get('/membership/my-membership');
      console.log('📋 Full response object:', response);
      console.log('📋 Response type:', typeof response);
      
      // Our API service returns the parsed JSON directly, not wrapped in .data
      if (response && response.success && response.membership !== undefined) {
        console.log('📋 Setting subscription:', response.membership);
        setSubscription(response.membership);
      } else {
        console.log('📋 No membership data found in response');
        console.log('📋 Response structure:', Object.keys(response || {}));
        setSubscription(null);
      }
    } catch (error) {
      console.error('❌ Error fetching subscription:', error);
      console.error('❌ Error details:', error.message);
      console.error('❌ Error response:', error.response);
      console.error('❌ Error status:', error.response?.status);
      console.error('❌ Error data:', error.response?.data);

      // If unauthorized, clear token and redirect to login
      if (error.response?.status === 401) {
        console.log('🔄 Token invalid/expired - clearing and redirecting to login');
        localStorage.removeItem('token');
        // Don't redirect automatically, let user handle login
      }

      setSubscription(null);
    }
  }, []);

  const checkInspectionCredits = () => {
    if (!subscription || (subscription.status !== 'ACTIVE' && subscription.status !== 'PENDING')) {
      return false;
    }
    
    const tier = subscription.tier;
    if (!tier || !tier.features) {
      return false;
    }
    
    const monthlyInspections = 1; // Only 1 inspection per month
    const usedThisMonth = subscription.currentUsage?.inspectionCreditsUsed || 0;
    
    return usedThisMonth < monthlyInspections;
  };

  const handleRequestInspection = () => {
    if (checkInspectionCredits()) {
      setShowRequestModal(true);
    } else {
      if (subscription?.status === 'PENDING') {
        alert('Your membership is still being processed. Please contact support to activate your membership first.');
      } else {
        navigate('/contact');
      }
    }
  };

  useEffect(() => {
    fetchInspections();
    fetchSubscription();
  }, [fetchInspections, fetchSubscription]);

  const getStatusBadge = (status) => {
    const badges = {
      PENDING_SCHEDULE: 'bg-yellow-100 text-yellow-800',
      SCHEDULED: 'bg-blue-100 text-blue-800',
      COMPLETED: 'bg-green-100 text-green-800',
      CANCELLED: 'bg-red-100 text-red-800',
      MISSED: 'bg-gray-100 text-gray-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const formatStatus = (status) => {
    return status.replace('_', ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">My Inspections</h1>
            <p className="mt-2 text-gray-600">
              View and manage your monthly property inspections
            </p>
            {/* Debug info */}
            <div className="mt-4 p-4 bg-gray-100 rounded-lg text-sm">
              <p><strong>Debug Info:</strong></p>
              <p>Subscription: {subscription ? 'Found' : 'Not found'}</p>
              <p>Loading: {loading ? 'Yes' : 'No'}</p>
              <p>Token exists: {localStorage.getItem('token') ? 'Yes' : 'No'}</p>
              {subscription && (
                <>
                  <p>Status: {subscription.status}</p>
                  <p>Tier: {subscription.tier?.displayName || 'N/A'}</p>
                  <p>Monthly Inspections: 1</p>
                  <p>Used This Month: {subscription.currentUsage?.inspectionCreditsUsed || 0}</p>
                  <p>Has Credits: {checkInspectionCredits() ? 'Yes' : 'No'}</p>
                </>
              )}
            </div>
          </div>
          <button
            onClick={handleRequestInspection}
            className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200 flex items-center space-x-2 shadow-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span>
              {checkInspectionCredits() 
                ? (subscription?.status === 'ACTIVE' ? 'Request Monthly Inspection' : 'Request Inspection (Membership Pending)') 
                : 'Request Inspection Support'
              }
            </span>
          </button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {['ALL', 'PENDING_SCHEDULE', 'SCHEDULED', 'COMPLETED'].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  filter === status
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {status === 'ALL' ? 'All Inspections' : formatStatus(status)}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800">{error}</div>
        </div>
      )}

      {/* Inspections List */}
      <div className="space-y-6">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-500">Loading inspections...</p>
          </div>
        ) : (!Array.isArray(inspections) || inspections.length === 0) ? (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No inspections found</h3>
            <p className="text-gray-500">
              {filter === 'ALL'
                ? 'You don\'t have any inspections yet.'
                : `No ${formatStatus(filter).toLowerCase()} inspections found.`
              }
            </p>
          </div>
        ) : (
          inspections.map((inspection) => (
            inspection && inspection._id ? (
            <div key={inspection._id} className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <h3 className="text-lg font-medium text-gray-900">
                      {inspection.inspectionMonth || 'Unknown'} Inspection
                    </h3>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(inspection.status || 'PENDING_SCHEDULE')}`}>
                      {formatStatus(inspection.status || 'PENDING_SCHEDULE')}
                    </span>
                  </div>

                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Property</p>
                      <p className="text-sm font-medium text-gray-900">{inspection.propertyAddress || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Property Type</p>
                      <p className="text-sm font-medium text-gray-900">{inspection.propertyType || 'N/A'}</p>
                    </div>

                    {inspection.scheduledDate && (
                      <>
                        <div>
                          <p className="text-sm text-gray-500">Scheduled Date</p>
                          <p className="text-sm font-medium text-gray-900">
                            {format(new Date(inspection.scheduledDate), 'PPP')}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Scheduled Time</p>
                          <p className="text-sm font-medium text-gray-900">{inspection.scheduledTime || 'N/A'}</p>
                        </div>
                      </>
                    )}

                    {inspection.inspector && (
                      <div className="md:col-span-2">
                        <p className="text-sm text-gray-500">Inspector</p>
                        <p className="text-sm font-medium text-gray-900">
                          {inspection.inspector.firstName} {inspection.inspector.lastName}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="ml-6 flex flex-col space-y-2">
                  {inspection.status === 'PENDING_SCHEDULE' && (
                    <button
                      onClick={() => navigate(`/dashboard/inspections/${inspection._id}/schedule`)}
                      className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700"
                    >
                      Schedule
                    </button>
                  )}

                  {inspection.status === 'COMPLETED' && (
                    <button
                      onClick={() => navigate(`/dashboard/inspections/${inspection._id}`)}
                      className="bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-700"
                    >
                      View Report
                    </button>
                  )}

                  {inspection.status === 'SCHEDULED' && (
                    <button
                      onClick={() => navigate(`/dashboard/inspections/${inspection._id}`)}
                      className="bg-gray-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-700"
                    >
                      View Details
                    </button>
                  )}
                </div>
              </div>
            </div>
            ) : null
          ))
        )}
      </div>

      {/* Inspection Request Modal */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Request Monthly Inspection</h3>
              <button
                onClick={() => setShowRequestModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-4">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Property Type
                  </label>
                  <select
                    value={requestForm.propertyType}
                    onChange={(e) => setRequestForm({...requestForm, propertyType: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">Select property type</option>
                    <option value="HDB">HDB</option>
                    <option value="Condo">Condo</option>
                    <option value="Landed">Landed</option>
                    <option value="Commercial">Commercial</option>
                  </select>
                </div>

                {/* Date Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Preferred Date (Current Month Only)
                  </label>
                  <input
                    type="date"
                    value={selectedDate || ''}
                    min={new Date().toISOString().split('T')[0]}
                    max={new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0]}
                    onChange={(e) => {
                      setSelectedDate(e.target.value);
                      setRequestForm({...requestForm, scheduledDate: e.target.value});
                      if (e.target.value) {
                        fetchAvailableSlots(e.target.value);
                      }
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    October 2025 only - {new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate()} days available
                  </p>
                </div>

                {/* Time Slot Selection */}
                {selectedDate && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Preferred Time (9:00 AM - 6:00 PM)
                    </label>
                    <div className="grid grid-cols-4 gap-2 max-h-40 overflow-y-auto">
                      {timeSlots.map((time) => {
                        const isBooked = isSlotBooked(time);
                        const isSelected = requestForm.scheduledTime === time;
                        return (
                          <button
                            key={time}
                            type="button"
                            disabled={isBooked}
                            onClick={() => setRequestForm({...requestForm, scheduledTime: time})}
                            className={`p-2 text-sm rounded border ${
                              isBooked
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50'
                                : isSelected
                                ? 'bg-orange-600 text-white border-orange-600'
                                : 'bg-white text-gray-700 border-gray-300 hover:border-orange-500 hover:bg-orange-50'
                            } transition-colors`}
                          >
                            {time}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Urgency Level
                  </label>
                  <select
                    value={requestForm.urgency}
                    onChange={(e) => setRequestForm({...requestForm, urgency: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="low">Low - Within 2 weeks</option>
                    <option value="normal">Normal - Within 1 week</option>
                    <option value="high">High - Within 3 days</option>
                    <option value="urgent">Urgent - Within 24 hours</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Additional Details (Optional)
                  </label>
                  <textarea
                    value={requestForm.description}
                    onChange={(e) => setRequestForm({...requestForm, description: e.target.value})}
                    placeholder="Describe any specific areas of concern or requirements..."
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    rows={3}
                  />
                </div>
              </div>

              {subscription && (
                <div className="bg-blue-50 p-3 rounded-md mt-4">
                  <p className="text-sm text-blue-800">
                    <strong>Monthly Credits:</strong> {1 - (subscription.currentUsage?.inspectionCreditsUsed || 0)} remaining this month
                  </p>
                  {(subscription.currentUsage?.inspectionCreditsUsed || 0) >= 1 && (
                    <p className="text-sm text-red-600 mt-1">
                      You have used your monthly inspection credit. Next credit available next month.
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowRequestModal(false);
                  setSelectedDate(null);
                  setRequestForm({
                    propertyType: '',
                    urgency: 'normal',
                    description: '',
                    scheduledDate: '',
                    scheduledTime: ''
                  });
                }}
                className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!requestForm.propertyType) {
                    alert('Please select a property type');
                    return;
                  }
                  if (!requestForm.scheduledDate) {
                    alert('Please select a preferred date');
                    return;
                  }
                  if (!requestForm.scheduledTime) {
                    alert('Please select a preferred time');
                    return;
                  }

                  setRequestLoading(true);
                  try {
                    // Make API call to request the inspection
                    await api.post('/inspections/customer/request', {
                      propertyType: requestForm.propertyType,
                      urgency: requestForm.urgency,
                      description: requestForm.description,
                      scheduledDate: requestForm.scheduledDate,
                      scheduledTime: requestForm.scheduledTime
                    });

                    // Reset form
                    setRequestForm({
                      propertyType: '',
                      urgency: 'normal',
                      description: '',
                      scheduledDate: '',
                      scheduledTime: ''
                    });
                    setSelectedDate(null);
                    setShowRequestModal(false);
                    
                    // Refresh inspections list
                    fetchInspections();
                    
                    alert('Inspection request submitted successfully! You will receive a confirmation email shortly.');
                  } catch (error) {
                    console.error('Error requesting inspection:', error);
                    const errorMessage = error.response?.data?.message || 'Failed to submit inspection request. Please try again.';
                    alert(errorMessage);
                  } finally {
                    setRequestLoading(false);
                  }
                }}
                disabled={requestLoading}
                className="flex-1 bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 transition-colors disabled:opacity-50"
              >
                {requestLoading ? 'Requesting...' : 'Request Inspection'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerInspections;